import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FolderOpen, 
  FileText, 
  CheckCircle, 
  Plus,
  ArrowRight,
  TrendingUp
} from 'lucide-react';

const DashboardPage = () => {
  const [stats, setStats] = useState({
    total_cases: 0,
    active_cases: 0,
    total_entries: 0,
    total_document_types: 0
  });
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, casesRes] = await Promise.all([
        api.get('/stats'),
        api.get('/cases')
      ]);
      setStats(statsRes.data);
      setRecentCases(casesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Cases', 
      value: stats.total_cases, 
      icon: FolderOpen, 
      color: 'bg-blue-500',
      description: 'All cases in system'
    },
    { 
      title: 'Active Cases', 
      value: stats.active_cases, 
      icon: CheckCircle, 
      color: 'bg-green-500',
      description: 'Currently active'
    },
    { 
      title: 'Document Entries', 
      value: stats.total_entries, 
      icon: FileText, 
      color: 'bg-purple-500',
      description: 'Total entries created'
    },
    { 
      title: 'Document Types', 
      value: stats.total_document_types, 
      icon: TrendingUp, 
      color: 'bg-amber-500',
      description: 'Available templates'
    },
  ];

  if (loading) {
    return (
      <div className="space-y-8" data-testid="dashboard-loading">
        {/* Header skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="h-9 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-5 bg-slate-100 rounded w-72 mt-2 animate-pulse" />
          </div>
          <div className="h-10 bg-slate-200 rounded w-32 animate-pulse" />
        </div>
        {/* Stats skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start justify-between animate-pulse">
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-24" />
                    <div className="h-8 bg-slate-200 rounded w-16 mt-1" />
                    <div className="h-3 bg-slate-100 rounded w-28 mt-1" />
                  </div>
                  <div className="h-12 w-12 bg-slate-200 rounded-lg" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Recent cases skeleton */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-36 mb-6" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 py-4 border-t border-slate-100 first:border-0">
                <div className="w-10 h-10 bg-slate-200 rounded-lg" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-slate-200 rounded w-40" />
                  <div className="h-3 bg-slate-100 rounded w-28" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Welcome to Sadananda & Co DDR System</p>
        </div>
        <Link to="/cases">
          <Button className="bg-[#1e3a5f] hover:bg-[#162c46]" data-testid="new-case-btn">
            <Plus size={18} className="mr-2" />
            New Case
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="border-0 shadow-sm hover:shadow-md transition-shadow"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                  <p className="text-3xl font-bold text-[#1e3a5f] mt-1">{stat.value}</p>
                  <p className="text-xs text-slate-400 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon size={24} className="text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Cases */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Recent Cases
          </CardTitle>
          <Link to="/cases">
            <Button variant="ghost" size="sm" className="text-[#1e3a5f]">
              View All
              <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentCases.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No cases yet. Create your first case to get started.</p>
              <Link to="/cases">
                <Button className="mt-4 bg-[#1e3a5f] hover:bg-[#162c46]">
                  <Plus size={18} className="mr-2" />
                  Create Case
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentCases.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/cases/${caseItem.id}`}
                  className="flex items-center justify-between py-4 hover:bg-slate-50 -mx-6 px-6 transition-colors"
                  data-testid={`case-item-${caseItem.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
                      <FolderOpen size={20} className="text-[#1e3a5f]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{caseItem.case_name}</p>
                      <p className="text-sm text-slate-500">{caseItem.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      caseItem.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {caseItem.status}
                    </span>
                    <ArrowRight size={16} className="text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
